import { MutationCache, QueryCache, QueryClient } from "@tanstack/react-query";
import { HTTPError } from "ky";
import type { User } from "@/api/types";
import { isErrorEnvelope } from "@/lib/handle-form-error";
import {
  assertCurrentSessionMutation,
  isCurrentSessionMutation,
  observeSessionUser,
  recordSessionMutation,
  retireSessionMutation,
  trackSessionMutations,
  transitionSession,
} from "@/lib/session-transition";

function isNoBusinessAccess(error: HTTPError): boolean {
  return (
    error.response.status === 403 &&
    isErrorEnvelope(error.data) &&
    error.data.errors.some((entry) => entry.code === "no_business_access")
  );
}

function isDashboardOverviewQuery(queryKey: readonly unknown[]): boolean {
  return queryKey[0] === "dashboard" && queryKey.at(-1) === "overview";
}

function isSignInRequest(error: HTTPError): boolean {
  const path = new URL(error.request.url).pathname;
  return (
    path === "/api/v1/sessions" ||
    path === "/api/v1/users" ||
    path.startsWith("/api/v1/auth/phone/")
  );
}

export function createQueryClient() {
  const client = new QueryClient({
    mutationCache: new MutationCache({
      onMutate: (_variables, mutation, context) => {
        recordSessionMutation(context, mutation);
      },
      onSuccess: (_data, _variables, _context, mutation) => {
        assertCurrentSessionMutation(mutation);
      },
      onError: (error, _variables, _context, mutation) => {
        if (!isCurrentSessionMutation(mutation)) {
          retireSessionMutation(mutation);
          return;
        }
        if (
          error instanceof HTTPError &&
          error.response.status === 401 &&
          !isSignInRequest(error)
        ) {
          transitionSession(client, null);
          // The public verification page renders its signed-out state and
          // keeps the email link available for the next sign-in.
          if (window.location.pathname !== "/verify-email") {
            window.location.href = "/auth/sign-in";
          }
        }
      },
      onSettled: (_data, error, _variables, _context, mutation) => {
        if (error && !isCurrentSessionMutation(mutation)) {
          retireSessionMutation(mutation);
        } else if (!error) {
          assertCurrentSessionMutation(mutation);
        }
      },
    }),
    queryCache: new QueryCache({
      onSuccess: (data, query) => {
        if (client.getQueryCache().get(query.queryHash) !== query) {
          throw new DOMException(
            "The account or business changed. Please try again.",
            "AbortError",
          );
        }
        if (query.queryKey.length === 1 && query.queryKey[0] === "me") {
          observeSessionUser(client, data as User | null);
        }
      },
      onError: (error, query) => {
        if (client.getQueryCache().get(query.queryHash) !== query) return;
        if (!(error instanceof HTTPError)) return;

        if (error.response.status === 401 && !query.queryKey.includes("me")) {
          transitionSession(client, null);
          window.location.href = "/auth/sign-in";
          return;
        }

        if (
          isNoBusinessAccess(error) &&
          query.queryKey[0] === "dashboard" &&
          !isDashboardOverviewQuery(query.queryKey) &&
          !window.location.pathname.startsWith("/onboarding")
        ) {
          transitionSession(client, client.getQueryData<User | null>(["me"]));
          window.location.href = "/onboarding/start";
        }
      },
    }),
    defaultOptions: {
      mutations: {
        // Writes must fail visibly offline and require a new user submission.
        networkMode: "always",
      },
      queries: {
        retry: (failureCount, error) => {
          if (error instanceof DOMException && error.name === "AbortError") return false;
          if (error instanceof HTTPError && error.response.status < 500) {
            return false;
          }
          return failureCount < 3;
        },
        staleTime: 0,
      },
    },
  });
  client.getMutationCache().subscribe((event) => trackSessionMutations(client, event));
  return client;
}
